import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";

interface LocationState { from?: string }

// WaffySSO login screen. No more username/password — a single button kicks off
// the Keycloak Authorization-Code + PKCE redirect against the waffy-staff realm
// (Google Workspace is the only IdP behind it).
export function LoginPage() {
  const { login, state } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (state.status === "authenticated") {
    const dest = (loc.state as LocationState | null)?.from ?? "/";
    nav(dest, { replace: true });
    return null;
  }

  const onSignIn = async () => {
    setError(null);
    setSubmitting(true);
    try {
      const from = (loc.state as LocationState | null)?.from;
      await login(from); // passes original destination through OIDC state so callback can restore it
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Sign-in failed");
      setSubmitting(false);
    }
  };

  return (
    <div className="login-shell">
      <div className="login-card">
        <div className="login-brand">
          <svg className="brand-mark" width="48" height="48" viewBox="0 0 36 36" aria-hidden="true">
            <defs>
              <linearGradient id="wgrad-login" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#1f5fe0" />
                <stop offset="100%" stopColor="#4f86f0" />
              </linearGradient>
            </defs>
            <rect x="1" y="1" width="34" height="34" rx="9" fill="url(#wgrad-login)" />
            <path
              d="M9.5 12 L13.5 24 L18 16 L22.5 24 L26.5 12"
              stroke="#0b1120" strokeWidth="2.6" fill="none"
              strokeLinecap="round" strokeLinejoin="round"
            />
          </svg>
          <div>
            <div className="brand-name">Waffy</div>
            <div className="brand-sub">Command Center</div>
          </div>
        </div>

        {error && <div className="login-error">{error}</div>}

        <button
          type="button"
          className="btn-primary login-submit"
          onClick={onSignIn}
          disabled={submitting}
          autoFocus
        >
          {submitting ? "Redirecting…" : "Sign in with Waffy SSO"}
        </button>

        <div className="login-foot">
          Staff sign-in via <code>id.waffyapp.com</code> · Google Workspace ·
          requires <code>staff-ops</code> or <code>staff-engineer</code>
        </div>
      </div>
    </div>
  );
}
